import React, { useCallback, useEffect, useState } from 'react';
import { api, errText } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Copy, KeyRound, Loader2, ShieldCheck, ShieldOff, Trash2, UserPlus, SlidersHorizontal } from 'lucide-react';

const SEAT_LABELS = { doctor: 'Doctor', admin: 'Admins', staff: 'Medical staff' };

function PermissionsDialog({ user, keys, onSaved }) {
  const [open, setOpen] = useState(false);
  const [perms, setPerms] = useState({});
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPerms({ ...(user.effective_permissions || {}) });
      setPhone(user.phone || '');
    }
  }, [open, user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { permissions: perms, phone });
      toast.success(`Settings updated for ${user.name}`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(errText(e, 'Could not save settings'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`admin-user-perms-${user.id}`}><SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Permissions</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="admin-perms-dialog">
        <DialogHeader>
          <DialogTitle>Permissions — {user.name}</DialogTitle>
          <DialogDescription>Choose exactly what this {user.role} is allowed to do. Toggles override their role defaults.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5 max-h-[50vh] overflow-y-auto pr-1">
          {Object.entries(keys || {}).map(([k, label]) => (
            <div key={k} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
              <span className="text-sm">{label}</span>
              <Switch checked={!!perms[k]} onCheckedChange={(v) => setPerms((p) => ({ ...p, [k]: v }))} data-testid={`admin-perm-toggle-${k}`} />
            </div>
          ))}
          <div className="rounded-lg border px-3 py-2.5 space-y-1.5">
            <Label className="text-xs font-medium">Mobile for SMS work alerts (optional)</Label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" data-testid="admin-user-phone-input" />
            <p className="text-[11px] text-muted-foreground">Gets texted for new cases / signature requests. Leave blank to receive email only.</p>
          </div>
        </div>
        <Button onClick={save} disabled={busy} data-testid="admin-perms-save">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { practice, user: me } = useApp();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [seats, setSeats] = useState(null);
  const [role, setRole] = useState('doctor');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, i, s] = await Promise.all([
        api.get('/users').then(r => r.data || []),
        api.get('/invites').then(r => r.data || []),
        api.get('/users/seats').then(r => r.data).catch(() => null),
      ]);
      setUsers(u);
      setInvites(i);
      setSeats(s);
    } catch (e) { /* handled by interceptor */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The price of one more seat, from the effective plan. Empty when the seat
  // summary could not load (no manage_users) rather than a guessed number.
  const seatPrice = (k) => {
    const inv = seats?.invoice;
    if (!inv?.overage_prices || inv.included_seats?.[k] === undefined) return '';
    return ` ($${Number(inv.overage_prices[k] ?? 0).toFixed(0)}/mo beyond ${inv.included_seats[k]})`;
  };

  const createInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/invites', { role, email, invitee_name: name });
      const emailed = r.data?.email_result?.event === 'email_sent';
      toast.success(emailed ? `Invite created and emailed to ${email}` : 'Invite link created — copy it from the list below');
      if (r.data?.overage_notice) toast.warning(r.data.overage_notice, { duration: 8000 });
      setEmail(''); setName('');
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not create invite'));
    } finally {
      setBusy(false);
    }
  };

  const toggleUser = async (u, on) => {
    try {
      await api.patch(`/users/${u.id}`, { disabled: !on });
      toast.success(!on ? `${u.name} switched off — their seat is freed and billing adjusts automatically` : `${u.name} switched on`);
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not update user'));
    }
  };

  const copyLink = async (inv) => {
    const url = `${window.location.origin}${inv.link_path || `/invite/${inv.token}`}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied to clipboard');
    } catch (e) {
      toast.info(url);
    }
  };

  const resetLink = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/reset-link`);
      const url = `${window.location.origin}${r.data.link_path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Reset link for ${u.name} copied — valid for 60 minutes`);
      } catch (e) {
        toast.info(url);
      }
    } catch (err) {
      toast.error(errText(err, 'Could not generate a reset link'));
    }
  };

  const resetMfa = async (u) => {
    try {
      await api.post(`/users/${u.id}/mfa-reset`);
      toast.success(`Two-factor authentication reset for ${u.name} — they can sign in with password only and re-enroll`);
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not reset MFA'));
    }
  };

  const revoke = async (inv) => {
    try {
      await api.delete(`/invites/${inv.id}`);
      toast.success('Invite revoked');
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not revoke invite'));
    }
  };

  const pending = invites.filter(i => i.status === 'pending');

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-5">
      <div>
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">Users & Invitations</h1>
        <p className="text-sm text-muted-foreground">Manage who has access to {practice?.name || 'your practice'}. Share invite links directly — automatic email delivery arrives with the email integration.</p>
      </div>

      {seats?.invoice && (
        <Card data-testid="admin-seats-card">
          <CardContent className="pt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
            {['doctor', 'admin', 'staff'].map((k) => {
              const used = seats.invoice.seat_usage[k];
              const inc = seats.invoice.included_seats[k];
              const over = Math.max(0, used - inc);
              return (
                <div key={k} className="text-sm">
                  <span className="text-muted-foreground">{SEAT_LABELS[k]}: </span>
                  <span className="font-medium tabular-nums">{used} / {inc} included</span>
                  {over > 0 && <Badge variant="outline" className="ml-1.5 text-[10px] text-amber-600 border-amber-500/40">+{over} billed</Badge>}
                </div>
              );
            })}
            <div className="ml-auto text-sm">
              <span className="text-muted-foreground">Your monthly total: </span>
              <span className="font-semibold tabular-nums" data-testid="admin-seats-total">${Number(seats.invoice.total_monthly).toFixed(2)}</span>
            </div>
            {/* Read from the effective plan (owner pricing + any negotiated deal),
                never hardcoded — a repriced practice would otherwise be told the old numbers. */}
            <p className="w-full text-[11px] text-muted-foreground">
              Extra seats beyond those included: {['doctor', 'admin', 'staff'].map((k) => `${k} $${Number(seats.invoice.overage_prices?.[k] ?? 0).toFixed(2)}/mo`).join(', ')} — added to your bill automatically. Switching a user off frees their seat.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" /> Invite a team member</CardTitle>
          <CardDescription className="text-xs">Creates a secure link valid for 7 days. The invitee sets their own password.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={createInvite} className="grid grid-cols-1 sm:grid-cols-[130px_1fr_1fr_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="admin-invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="doctor">Doctor{seatPrice('doctor')}</SelectItem>
                  <SelectItem value="nurse">Medical staff{seatPrice('staff')}</SelectItem>
                  <SelectItem value="admin">Admin{seatPrice('admin')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Alex Morgan" data-testid="admin-invite-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Email (optional)</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@yourpractice.com" data-testid="admin-invite-email-input" />
            </div>
            <Button type="submit" disabled={busy} data-testid="admin-invite-create-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create link'}
            </Button>
          </form>

          {pending.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="admin-invite-list">
              {pending.map((inv) => (
                <li key={inv.id} className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm" data-testid={`admin-invite-row-${inv.id}`}>
                  <Badge variant="secondary" className="capitalize">{inv.role}</Badge>
                  <span className="flex-1 truncate text-muted-foreground">{inv.invitee_name || inv.email || 'Unnamed invite'} · expires {(inv.expires_at || '').slice(0, 10)}</span>
                  <Button size="sm" variant="outline" onClick={() => copyLink(inv)} data-testid={`admin-invite-copy-${inv.id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => revoke(inv)} aria-label="Revoke invite" data-testid={`admin-invite-revoke-${inv.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Practice Users</CardTitle></CardHeader>
        <CardContent>
          {/* Phone: card list */}
          <div className="md:hidden space-y-3" data-testid="admin-users-cards">
            {users.map(u => (
              <div key={u.id} className={`rounded-lg border p-3 space-y-3 ${u.disabled ? 'opacity-50' : ''}`} data-testid={`admin-user-card-${u.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{u.name}{u.id === me?.id && <Badge variant="secondary" className="ml-1.5 text-[9px]">You</Badge>}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <Badge variant="secondary" className="capitalize shrink-0">{u.role}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground inline-flex items-center gap-1">
                    {u.mfa_enabled ? <><ShieldCheck className="h-3 w-3" /> MFA on</> : 'MFA off'}
                  </span>
                  <label className="inline-flex items-center gap-2 text-muted-foreground">
                    Active <Switch checked={!u.disabled} disabled={u.id === me?.id} onCheckedChange={(v) => toggleUser(u, v)} data-testid={`admin-user-toggle-card-${u.id}`} />
                  </label>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <PermissionsDialog user={u} keys={seats?.permission_keys} onSaved={load} />
                  <Button size="sm" variant="outline" onClick={() => resetLink(u)} data-testid={`admin-user-reset-link-card-${u.id}`}><KeyRound className="h-3.5 w-3.5 mr-1" /> Reset link</Button>
                  {u.mfa_enabled && <Button size="sm" variant="outline" onClick={() => resetMfa(u)} data-testid={`admin-user-reset-mfa-card-${u.id}`}><ShieldOff className="h-3.5 w-3.5 mr-1" /> Reset MFA</Button>}
                </div>
              </div>
            ))}
          </div>

          {/* Tablet and up: table */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-card border-b"><tr className="text-left text-xs text-muted-foreground"><th className="px-3 py-2">Name</th><th className="px-3 py-2">Role</th><th className="px-3 py-2 hidden md:table-cell">Email</th><th className="px-3 py-2 hidden lg:table-cell">MFA</th><th className="px-3 py-2">Active</th><th className="px-3 py-2">Manage</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className={`border-b ${u.disabled ? 'opacity-50' : ''}`} data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-3 py-2 font-medium">{u.name}{u.id === me?.id && <Badge variant="secondary" className="ml-1.5 text-[9px]">You</Badge>}</td>
                  <td className="px-3 py-2 capitalize">{u.role}</td>
                  <td className="px-3 py-2 hidden md:table-cell text-muted-foreground">{u.email}</td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    {u.mfa_enabled
                      ? <Badge className="text-[10px]" data-testid={`admin-user-mfa-on-${u.id}`}><ShieldCheck className="h-3 w-3 mr-1" /> On</Badge>
                      : <span className="text-xs text-muted-foreground">Off</span>}
                  </td>
                  <td className="px-3 py-2">
                    <Switch checked={!u.disabled} disabled={u.id === me?.id} onCheckedChange={(v) => toggleUser(u, v)} data-testid={`admin-user-toggle-${u.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2 flex-wrap">
                      <PermissionsDialog user={u} keys={seats?.permission_keys} onSaved={load} />
                      <Button size="sm" variant="outline" onClick={() => resetLink(u)} data-testid={`admin-user-reset-link-${u.id}`}>
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset link
                      </Button>
                      {u.mfa_enabled && (
                        <Button size="sm" variant="outline" onClick={() => resetMfa(u)} data-testid={`admin-user-reset-mfa-${u.id}`}>
                          <ShieldOff className="h-3.5 w-3.5 mr-1" /> Reset MFA
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
